import { Link } from '@tanstack/react-router'
import { PageHeader, StatusBadge } from '../components/ui'
import { useHealth, useReadiness } from '../lib/health'
import { isTexStaff, useMe } from '../lib/session'

export function HomePage() {
  const health = useHealth()
  const ready = useReadiness()
  const me = useMe()
  const user = me.data ?? null

  return (
    <section className="space-y-6">
      <PageHeader
        eyebrow="Tex Finance"
        title="Dealer financing desk"
        description={
          user
            ? `Signed in as ${user.user.email}${user.tex_role ? ` (Tex ${user.tex_role.replace('_', ' ')})` : ''}.`
            : 'Dealer, borrower and Tex reviewer flows land in the M1–M3 pull requests described in docs/PLAN.md.'
        }
      />

      {user && (
        <div className="space-y-2 rounded-xl border border-[var(--border-subtle)] bg-white p-5 text-sm shadow-sm [&_a]:text-tex-blue [&_a]:no-underline [&_a:hover]:underline">
          {isTexStaff(user) && (
            <p>
              <Link to="/dealers" className="font-medium underline">
                Review dealers
              </Link>{' '}
              — verify new dealerships, suspend or reinstate{user.tex_role === 'ops_admin' ? '; onboard dealers and seed their owner' : ''}.
            </p>
          )}
          {(isTexStaff(user) || user.dealers.length > 0) && (
            <p>
              <Link to="/cases" search={{}} className="font-medium underline">
                Financing requests
              </Link>{' '}
              — {isTexStaff(user) ? 'every request dealers have opened.' : 'open a request for a buyer and track it through funding.'}
            </p>
          )}
          {user.cases.length > 0 && (
            <p>
              <Link to="/my-cases" className="font-medium underline">
                Your financing
              </Link>{' '}
              — {user.cases.length === 1 ? 'a request a dealer shared with you.' : `${user.cases.length} requests dealers shared with you.`}
            </p>
          )}
          {user.dealers.map((d) => (
            <p key={d.dealer_id} className="flex items-center gap-2">
              <Link to="/dealers/$dealerId" params={{ dealerId: d.dealer_id }} className="font-medium underline">
                Your dealership
              </Link>
              <StatusBadge status={d.status} />
              <span className="text-neutral-500">you are {d.role === 'member' ? 'a member' : `an ${d.role}`}</span>
            </p>
          ))}
          {!isTexStaff(user) && user.dealers.length === 0 && user.cases.length === 0 && (
            <p className="text-neutral-500">
              Your account isn't attached to anything yet. Open the invitation or financing link you were sent while signed
              in as <strong>{user.user.email}</strong>, or ask your dealership's admin to invite that address.
            </p>
          )}
        </div>
      )}

      {!user && me.data === null && (
        <p className="text-sm">
          <Link to="/sign-in" className="font-medium underline">
            Sign in
          </Link>{' '}
          to continue.
        </p>
      )}

      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Stat
          label="API"
          value={health.isError ? 'unreachable' : health.data ? 'ok' : 'checking…'}
          accent={health.isError ? 'red' : 'blue'}
        />
        <Stat label="Environment" value={health.data?.environment ?? '—'} accent="yellow" />
        <Stat
          label="Database"
          value={ready.isError ? 'error' : ready.data ? ready.data.checks.database : 'checking…'}
          accent={ready.isError ? 'red' : 'blue'}
        />
      </dl>
    </section>
  )
}

const accentBar = {
  neutral: 'bg-neutral-300',
  red: 'bg-tex-red',
  blue: 'bg-tex-blue',
  yellow: 'bg-tex-yellow',
} as const

function Stat({ label, value, accent = 'neutral' }: { label: string; value: string; accent?: keyof typeof accentBar }) {
  return (
    <div className="relative overflow-hidden rounded-xl border border-[var(--border-subtle)] bg-white p-4 pl-5 shadow-sm">
      <span className={`absolute inset-y-0 left-0 w-1 ${accentBar[accent]}`} aria-hidden />
      <dt className="font-display text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</dt>
      <dd className="mt-1 font-mono text-lg font-medium tabular-nums text-neutral-900">{value}</dd>
    </div>
  )
}
